/**
 * Shared chunked-upload loop for the originator and custodian Ingest proxies.
 *
 * A single 200–250 MB PUT gets cut by the ingress mid-stream ("Failed to
 * fetch" at ~45 %). Files above CHUNK_UPLOAD_THRESHOLD_BYTES are sliced into
 * CHUNK_UPLOAD_SIZE_BYTES requests; only a failed chunk is retried, then one
 * `complete` call asks the server to assemble and hash the blob.
 */

export const CHUNK_UPLOAD_THRESHOLD_BYTES = 32 * 1024 * 1024;
export const CHUNK_UPLOAD_SIZE_BYTES = 8 * 1024 * 1024;
export const CHUNK_UPLOAD_MAX_ATTEMPTS = 4;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function chunkRetryable(error) {
  const status = typeof error?.status === 'number' ? error.status : 0;
  if (status >= 500 || status === 408 || status === 429) return true;
  if (status > 0) return false;
  return /failed to fetch|networkerror|load failed|network request failed/i.test(
    String(error?.message || '')
  );
}

export function shouldChunkUpload(data, threshold = CHUNK_UPLOAD_THRESHOLD_BYTES) {
  return Boolean(
    data &&
      typeof data.slice === 'function' &&
      typeof data.size === 'number' &&
      data.size > threshold
  );
}

/**
 * @param {Blob} file
 * @param {{
 *   putChunk: (index: number, slice: Blob) => Promise<unknown>,
 *   complete: (chunkCount: number) => Promise<object>,
 *   chunkSize?: number,
 *   maxAttempts?: number,
 *   onProgress?: (info: { uploadedBytes: number, totalBytes: number, chunkIndex: number, chunkCount: number }) => void,
 * }} options
 */
export async function uploadInChunks(
  file,
  {
    putChunk,
    complete,
    chunkSize = CHUNK_UPLOAD_SIZE_BYTES,
    maxAttempts = CHUNK_UPLOAD_MAX_ATTEMPTS,
    onProgress,
  }
) {
  const totalBytes = file.size;
  const size = Math.max(1, chunkSize);
  const chunkCount = Math.max(1, Math.ceil(totalBytes / size));

  for (let index = 0; index < chunkCount; index += 1) {
    const start = index * size;
    const end = Math.min(totalBytes, start + size);
    const slice = file.slice(start, end);
    let attempt = 0;
    for (;;) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await putChunk(index, slice);
        break;
      } catch (error) {
        attempt += 1;
        if (attempt >= maxAttempts || !chunkRetryable(error)) throw error;
        // eslint-disable-next-line no-await-in-loop
        await sleep(Math.min(8000, 500 * 2 ** (attempt - 1)));
      }
    }
    if (typeof onProgress === 'function') {
      onProgress({ uploadedBytes: end, totalBytes, chunkIndex: index, chunkCount });
    }
  }

  return complete(chunkCount);
}
